// ============================================================================
// THE COURTROOM — Trial Generation Engine
// ----------------------------------------------------------------------------
// Reads a confession, detects its "theme", and stages a mock trial:
// a charge, prosecution arguments, defense arguments, a verdict and a sentence.
// Fully client-side and deterministic-per-roll — no API keys required.
// ============================================================================

export type Verdict = 'GUILTY' | 'NOT GUILTY' | 'MISTRIAL'

export interface TrialResult {
  charge: string
  prosecution: string[]
  defense: string[]
  verdict: Verdict
  sentence: string
}

// A "case file" is a theme with its own flavor of arguments.
interface CaseFile {
  id: string
  keywords: string[]
  charges: string[]
  prosecution: string[]
  defense: string[]
  sentences: string[]
}

const pick = <T,>(arr: T[]): T => arr[Math.floor(Math.random() * arr.length)]

const pickTwo = <T,>(arr: T[]): T[] => {
  if (arr.length <= 2) return [...arr]
  const first = Math.floor(Math.random() * arr.length)
  let second = Math.floor(Math.random() * arr.length)
  while (second === first) second = Math.floor(Math.random() * arr.length)
  return [arr[first], arr[second]]
}

// ----------------------------------------------------------------------------
// CASE FILES — themed evidence lockers
// ----------------------------------------------------------------------------
const CASE_FILES: CaseFile[] = [
  {
    id: 'food',
    keywords: [
      'fries', 'food', 'ate', 'eat', 'snack', 'pizza', 'fridge', 'leftover',
      'leftovers', 'chip', 'chips', 'cookie', 'cookies', 'cake', 'lunch',
      'dinner', 'breakfast', 'bite', 'chocolate', 'candy', 'meal',
    ],
    charges: [
      'Grand Theft Snacko',
      'Unlawful Consumption in the First Degree',
      'Aggravated Munching',
      'Possession of Someone Else’s Calories',
    ],
    prosecution: [
      'The defendant knew — KNEW — that food was not theirs, and reached for it anyway.',
      'We have the crumbs. We have the motive. We have a very sad, empty container.',
      'This was not hunger. This was a premeditated act of deliciousness.',
      'Your Honor, the wrapper was still warm. The defendant struck while the food was hot.',
      'They didn’t even offer to share. Let the record show a total absence of remorse.',
    ],
    defense: [
      'My client submits that the food was unattended — practically abandoned.',
      'By the ancient law of the household, whatever is left in the open is fair game.',
      'The portion was small. The temptation was large. The math favors mercy.',
      'No “DO NOT EAT” note was affixed. In this country, that is basically consent.',
      'My client was merely preventing the tragedy of good food going cold.',
    ],
    sentences: [
      'Replace the fries. With extra. Delivered to their door.',
      'Cook the victim a full meal, garnish included.',
      'Surrender your own snacks for one (1) full week.',
      'A formal, hand-written apology to the empty container.',
    ],
  },
  {
    id: 'sleep',
    keywords: [
      'sleep', 'slept', 'nap', 'bed', 'snooze', 'alarm', 'overslept',
      'late', 'oversleep', 'tired', 'stayed up', 'up all night',
    ],
    charges: [
      'Reckless Snoozing',
      'Desertion of Duty via Nap',
      'Conspiracy to Stay in Bed',
      'Assault on the Alarm Clock',
    ],
    prosecution: [
      'The alarm rang nine times. NINE. Each snooze a fresh betrayal.',
      'The defendant chose the pillow over their responsibilities. Shameful.',
      'Witnesses report the defendant said “five more minutes” at 11:42 AM.',
      'This was not rest. This was a hostage situation, and the hostage was the morning.',
    ],
    defense: [
      'My client was simply honoring the sacred human need for rest.',
      'Science, Your Honor. My client was optimizing their REM cycles.',
      'The bed was warm. The world was cold. Any reasonable person would agree.',
      'Blame the alarm for being too easy to dismiss. My client is the true victim here.',
    ],
    sentences: [
      'Wake up at 6 AM sharp for one week. No snoozing.',
      'The alarm clock is to be placed across the room, permanently.',
      'Buy coffee for everyone you kept waiting.',
      'Community service: wake a friend up on time for a change.',
    ],
  },
  {
    id: 'phone',
    keywords: [
      'text', 'texted', 'message', 'reply', 'replied', 'ghost', 'ghosted',
      'call', 'called', 'phone', 'dm', 'left on read', 'read', 'seen',
      'ignored', 'notification', 'unread',
    ],
    charges: [
      'Reckless Ghosting',
      'Leaving a Human on Read',
      'Neglect of a Digital Duty',
      'Conspiracy to “See” and Not Respond',
    ],
    prosecution: [
      'The message was delivered. It was READ. And then — silence. Cold, cruel silence.',
      'Three days, Your Honor. Three days a person waited for the word “ok”.',
      'The defendant had time to post a story but not to answer a single text.',
      'They typed. The dots appeared. The dots vanished. Hope died with them.',
    ],
    defense: [
      'My client meant to reply. Intention is nine tenths of the law.',
      'The message required an emotionally complete answer. My client was gathering strength.',
      'Notifications are a lie. My client cannot be blamed for a broken system.',
      'My client was practicing the modern art of mindful, offline living.',
    ],
    sentences: [
      'Reply to every pending message within the hour. All of them.',
      'Send one thoughtful voice note as reparation.',
      'Phone privileges suspended during dinner for one week.',
      'A sincere “sorry for the late reply, that’s on me.”',
    ],
  },
  {
    id: 'work',
    keywords: [
      'work', 'email', 'meeting', 'deadline', 'boss', 'report', 'office',
      'project', 'slack', 'presentation', 'zoom', 'client', 'skipped work',
      'procrastinate', 'procrastinated', 'quit', 'lazy',
    ],
    charges: [
      'Professional Malingering',
      'Deadline Endangerment',
      'Impersonating a Productive Employee',
      'Aggravated Procrastination',
    ],
    prosecution: [
      'The deadline was clear. The calendar was red. The defendant opened a new browser tab.',
      'Fourteen tabs open, zero tasks done. The evidence speaks for itself.',
      'They replied “on it” at 9 AM and touched nothing until 4:59 PM.',
      'The meeting could have been an email. And yet, the defendant scheduled the meeting.',
    ],
    defense: [
      'My client works best under pressure. This was strategic pressure-building.',
      'Rest is productivity. My client was, in fact, working very hard at recovering.',
      'The task was ambiguous. One cannot be guilty of not doing an unclear thing.',
      'My client was simply protecting their peace in a hostile spreadsheet environment.',
    ],
    sentences: [
      'Finish the task. Today. Before you close a single tab.',
      'Send the status update you’ve been avoiding.',
      'Buy the whole team coffee for the delay.',
      'Inbox zero by end of day. All of it.',
    ],
  },
  {
    id: 'money',
    keywords: [
      'buy', 'bought', 'spend', 'spent', 'shopping', 'money', 'cart',
      'ordered', 'order', 'impulse', 'sale', 'checkout', 'splurge', 'expensive',
    ],
    charges: [
      'Reckless Endangerment of a Bank Account',
      'Impulse Purchase in the First Degree',
      'Conspiracy with a Shopping Cart',
      'Grand Theft (from Future You)',
    ],
    prosecution: [
      'It was on sale, they said. It was 40% off, they said. It was still money, Your Honor.',
      'The cart held three necessities and eleven emotional support items.',
      'The defendant clicked “Buy Now” faster than they’ve ever clicked anything.',
      'This purchase serves no purpose but the fleeting thrill of a doorbell.',
    ],
    defense: [
      'My client was stimulating the economy. This is patriotism, not crime.',
      'It sparked joy. Can we truly put a price on joy? The receipt says yes, but still.',
      'The discount was so steep that NOT buying would have been the real loss.',
      'Self-care isn’t free, Your Honor, and my client is worth every cent.',
    ],
    sentences: [
      'A strict no-spend week. Necessities only.',
      'Return one (1) item you cannot justify to a jury.',
      'Transfer the guilt amount into savings, immediately.',
      'Explain the purchase to your bank account out loud.',
    ],
  },
  {
    id: 'chores',
    keywords: [
      'dishes', 'laundry', 'clean', 'cleaning', 'chores', 'trash', 'mess',
      'vacuum', 'tidy', 'dish', 'washing', 'wash', 'sink', 'garbage',
    ],
    charges: [
      'Abandonment of a Sink Full of Dishes',
      'Willful Neglect of the Laundry Pile',
      'Harboring a Fugitive Mess',
      'Obstruction of a Clean Living Space',
    ],
    prosecution: [
      'The dishes have been “soaking” since Tuesday. It is now the following Tuesday.',
      'The laundry pile has developed its own weather system, Your Honor.',
      'The defendant walked past the trash six times, gaze fixed firmly forward.',
      '“I’ll do it later” — the last words of a thousand undone chores.',
    ],
    defense: [
      'My client operates on a flexible, artist’s schedule of cleanliness.',
      'The mess was building character. My client cultivates lived-in charm.',
      'One cannot rush the sacred ritual of “letting it soak.”',
      'My client was resting so they could clean at 100% capacity later. Efficiency.',
    ],
    sentences: [
      'Do every dish in that sink. Now. To completion.',
      'One full load of laundry, washed AND folded.',
      'Take out the trash and reflect on your choices.',
      'Deep clean one room of the victim’s choosing.',
    ],
  },
  {
    id: 'pet',
    keywords: [
      'dog', 'cat', 'pet', 'puppy', 'kitten', 'walk', 'fed', 'feed', 'litter',
      'treats', 'goldfish', 'hamster', 'bird',
    ],
    charges: [
      'Withholding Belly Rubs',
      'Neglect of an Extremely Good Boy',
      'Denial of a Second Dinner',
      'Emotional Distress of a Small Animal',
    ],
    prosecution: [
      'Those eyes, Your Honor. Those eyes begged, and the defendant looked away.',
      'The tail wagged in hope. The defendant did not deliver the treat. Barbaric.',
      'The walk was promised at noon. It is now dusk. The leash remains on the hook.',
      'A creature of pure love was made to wait. There is no lower crime.',
    ],
    defense: [
      'My client loves that animal more than most humans. This is context.',
      'The pet had already eaten. Twice. My client was being responsible.',
      'One cannot spoil an animal that is, by definition, already perfect.',
      'My client needed only five minutes. The dog, admittedly, disagreed.',
    ],
    sentences: [
      'Extra long walk. Best route. No cutting it short.',
      'One (1) premium treat, delivered with genuine praise.',
      'Belly rubs on demand for the remainder of the day.',
      'A full grooming and one heartfelt “who’s a good boy.”',
    ],
  },
  {
    id: 'social',
    keywords: [
      'party', 'friend', 'friends', 'canceled', 'cancelled', 'bailed', 'plans',
      'invited', 'flaked', 'flake', 'skipped', 'birthday', 'hangout', 'group',
    ],
    charges: [
      'Flaking with Malicious Comfort',
      'Bailing in the First Degree',
      'Breach of a Sacred Group Chat Agreement',
      'Desertion of Perfectly Good Plans',
    ],
    prosecution: [
      'The reservation was for six. Five showed up. One chose the couch.',
      'The defendant confirmed “100% coming” and then discovered blankets exist.',
      'They didn’t just cancel — they canceled forty minutes before. Cold.',
      'A group chat waited. The defendant left it on “typing…” for eternity.',
    ],
    defense: [
      'My client’s social battery was empty. You cannot pour from an empty cup.',
      'Rest is self-respect. My client chose peace over a crowded bar.',
      'The plans were vague. “We should hang out” is not a binding contract.',
      'My client sent a heartfelt cancellation. Effort, Your Honor, was present.',
    ],
    sentences: [
      'Plan the next hangout yourself, and actually show up.',
      'Buy the first round for everyone you stood up.',
      'One genuine, non-excuse apology to the group chat.',
      'No cancelling the next three plans. No exceptions.',
    ],
  },
  {
    id: 'lie',
    keywords: [
      'lie', 'lied', 'fib', 'excuse', 'pretend', 'pretended', 'fake', 'faked',
      'blame', 'blamed', 'sneak', 'sneaked', 'hid', 'hide',
    ],
    charges: [
      'Perjury in the Living Room',
      'Manufacturing a Convenient Excuse',
      'Fabrication of a Traffic Story',
      'Reckless Disregard for the Whole Truth',
    ],
    prosecution: [
      'The story had holes, Your Honor. Big ones. You could drive an excuse through them.',
      '“The traffic was terrible.” There was no traffic. There is never traffic.',
      'The defendant looked their victim in the eye and chose fiction.',
      'A small lie, they thought. But the jury knows how these things grow.',
    ],
    defense: [
      'It was a white lie — the gentlest, most breathable shade of dishonesty.',
      'My client lied to spare feelings. This is diplomacy, not deceit.',
      'The truth was boring. My client merely improved the narrative.',
      'No one was harmed. Except, arguably, the concept of trust.',
    ],
    sentences: [
      'Come clean. Full truth, no softening.',
      'One completely honest day. Brutal honesty only.',
      'Confess the real reason to the affected party.',
      'A sincere apology, no “but” attached.',
    ],
  },
  {
    id: 'health',
    keywords: [
      'gym', 'workout', 'run', 'exercise', 'diet', 'skipped the gym', 'water',
      'dentist', 'doctor', 'floss', 'flossed', 'jog', 'stretch',
    ],
    charges: [
      'Abandonment of a Gym Membership',
      'Reckless Skipping of Leg Day',
      'Conspiracy Against Your Own New Year’s Resolution',
      'Neglect of a Water Bottle',
    ],
    prosecution: [
      'The gym bag was packed. It sat by the door. It never moved. Neither did the defendant.',
      '“Tomorrow,” they said, for the fourteenth consecutive tomorrow.',
      'The running shoes are pristine, Your Honor. Suspiciously, tragically pristine.',
      'They told the doctor they “stay active.” The couch tells a different story.',
    ],
    defense: [
      'Rest days are essential. My client was simply having a rest month.',
      'My client walked to the fridge. That is, technically, cardio.',
      'The body is a temple. My client was giving the temple a well-earned holiday.',
      'Motivation is fickle. You cannot convict a person for lacking dopamine.',
    ],
    sentences: [
      'Actually go to the gym. Today. Bag already packed.',
      'One workout, fully completed, no scrolling between sets.',
      'Drink a full glass of water right now. Then another.',
      'Book the appointment you keep “meaning to book.”',
    ],
  },
]

// ----------------------------------------------------------------------------
// FALLBACK LOCKER — for confessions we can't neatly categorize
// ----------------------------------------------------------------------------
const GENERIC_CHARGES = [
  'General Mischief in the First Degree',
  'Suspicious Behavior of an Unspecified Nature',
  'Being a Little Bit of a Menace',
  'Conduct Unbecoming of a Reasonable Person',
  'Crimes Against Common Sense',
]

const GENERIC_PROSECUTION = [
  'Your Honor, the facts are simple, and the defendant knew exactly what they were doing.',
  'Let us not dress this up. There was intent. There was action. There were consequences.',
  'The defendant had every opportunity to choose the right path. They chose this one.',
  'We ask the jury to consider: who does this? Who, really, does this?',
  'A pattern, Your Honor. This is not a first offense — merely the first they confessed.',
  'The evidence is circumstantial only in the sense that the defendant surrounded themselves with it.',
]

const GENERIC_DEFENSE = [
  'My client is a good person who happened to make a very human decision.',
  'We ask the court: has anyone here truly never done the same? Cast the first stone.',
  'There was no malice. There was only a moment, and my client was simply in it.',
  'My client acted on instinct, and instinct, Your Honor, cannot be put on trial.',
  'Context is everything, and the context here is deeply, profoundly sympathetic.',
  'My client throws themselves on the mercy of the court, which is famously merciful.',
]

const GENERIC_SENTENCES = [
  'Apologize. Sincerely. Out loud. To an actual person.',
  'Perform one (1) act of unsolicited kindness before sundown.',
  'Sit quietly and reflect on the chain of events that led you here.',
  'Buy someone a coffee. You know who.',
  'Do the right thing next time. The court is watching.',
  'Time served. But the court remains deeply disappointed.',
]

// ----------------------------------------------------------------------------
// VERDICT LOGIC — weighted toward comedy, not fairness
// ----------------------------------------------------------------------------
function decideVerdict(): Verdict {
  const roll = Math.random()
  if (roll < 0.6) return 'GUILTY' // the courtroom is a harsh place
  if (roll < 0.88) return 'NOT GUILTY'
  return 'MISTRIAL'
}

const NOT_GUILTY_SENTENCES = [
  'Free to go — but the jury will remember your face.',
  'Acquitted on a technicality. Do not push your luck.',
  'Cleared of all charges. Celebrate responsibly.',
  'Not guilty. The victim, however, votes to appeal.',
]

const MISTRIAL_SENTENCES = [
  'A key witness burst into tears. Case dismissed in confusion.',
  'The jury could not stop laughing. Mistrial declared.',
  'The gavel broke mid-verdict. The universe demands a retrial.',
  'A juror knew the defendant. The whole thing collapsed. Try again.',
]

// ----------------------------------------------------------------------------
// MAIN ENGINE
// ----------------------------------------------------------------------------
export function generateTrial(rawConfession: string): TrialResult {
  const confession = rawConfession.trim()
  const lower = confession.toLowerCase()

  // Score each case file by keyword hits.
  let best: CaseFile | null = null
  let bestScore = 0
  for (const file of CASE_FILES) {
    let score = 0
    for (const kw of file.keywords) {
      if (lower.includes(kw)) score += 1
    }
    if (score > bestScore) {
      bestScore = score
      best = file
    }
  }

  const charge = best ? pick(best.charges) : pick(GENERIC_CHARGES)

  // Blend themed + generic arguments so no two trials feel identical.
  const prosecutionPool = best
    ? [...best.prosecution, GENERIC_PROSECUTION[0]]
    : GENERIC_PROSECUTION
  const defensePool = best
    ? [...best.defense, GENERIC_DEFENSE[0]]
    : GENERIC_DEFENSE

  const verdict = decideVerdict()

  let sentence: string
  if (verdict === 'GUILTY') {
    sentence = best ? pick(best.sentences) : pick(GENERIC_SENTENCES)
  } else if (verdict === 'NOT GUILTY') {
    sentence = pick(NOT_GUILTY_SENTENCES)
  } else {
    sentence = pick(MISTRIAL_SENTENCES)
  }

  return {
    charge,
    prosecution: pickTwo(prosecutionPool),
    defense: pickTwo(defensePool),
    verdict,
    sentence,
  }
}
